import React from "react";
import { SectionCard, Textarea, inputCls } from "./ui";
import { useProject } from "@/context/ProjectContext";
import { getMetrics } from "@/lib/metrics";
import SupportingMaterials from "./SupportingMaterials";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { Target, Flag, TrendingUp, ListChecks, Gauge } from "lucide-react";

const CHART_AXIS_FONT = 12;
const CHART_MARGIN = { top: 10, right: 10, left: -10, bottom: 0 };
const AXIS_TICK = { fontSize: CHART_AXIS_FONT, fill: "#64748b" };
const TOOLTIP_CURSOR = { fill: "#f1f5f9" };
const TOOLTIP_STYLE = { borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 13 };
const LEGEND_STYLE = { fontSize: 12 };
const BAR_RADIUS = [4, 4, 0, 0];

// Pure transform: combine Phase 1 metrics with Phase 6 post-intervention results.
function computeMetricResults(metrics, results) {
  return metrics.map((m) => {
    const baseline = parseFloat(m.current);
    const targetV = parseFloat(m.target);
    const post = parseFloat(results[m.id]);
    const initialGap = !isNaN(baseline) && !isNaN(targetV) ? targetV - baseline : null;
    const gapClosed = !isNaN(post) && !isNaN(baseline) ? post - baseline : null;
    const closePct = gapClosed !== null && initialGap ? Math.round((gapClosed / initialGap) * 100) : null;
    return { m, baseline, targetV, post, initialGap, gapClosed, closePct };
  });
}

function EvalKpis({ kpis }) {
  return (
    <div className="grid sm:grid-cols-3 gap-4">
      {kpis.map((k) => (
        <div key={k.label} className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm" data-testid={`eval-kpi-${k.label}`}>
          <div className="flex items-center justify-between">
            <span className="text-sm text-slate-500">{k.label}</span>
            <k.icon className="h-5 w-5 text-emerald-500" />
          </div>
          <p className="font-display text-3xl font-bold text-slate-900 mt-2">{k.value}</p>
          <p className="text-xs text-slate-400 mt-0.5">{k.sub}</p>
        </div>
      ))}
    </div>
  );
}

function MetricsEvalCard({ metrics, computed, results, setResult, chartData, postMetrics, onPostMetrics }) {
  return (
    <SectionCard title="All Metrics: Baseline vs Target vs Post-Intervention" desc="Every metric from Phase 1 is carried forward. Enter the post-intervention result for each." testid="phase6-chart">
      {metrics.length === 0 ? (
        <p data-testid="phase6-no-metrics" className="text-sm text-slate-400">No metrics defined in Phase 1 yet. Add metrics there to evaluate results here.</p>
      ) : (
        <>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={CHART_MARGIN}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis dataKey="name" tick={AXIS_TICK} axisLine={false} tickLine={false} />
                <YAxis tick={AXIS_TICK} axisLine={false} tickLine={false} />
                <Tooltip cursor={TOOLTIP_CURSOR} contentStyle={TOOLTIP_STYLE} />
                <Legend wrapperStyle={LEGEND_STYLE} />
                <Bar dataKey="Baseline" fill="#94a3b8" radius={BAR_RADIUS} maxBarSize={40} />
                <Bar dataKey="Post-Intervention" fill="#10b981" radius={BAR_RADIUS} maxBarSize={40} />
                <Bar dataKey="Target" fill="#0f172a" radius={BAR_RADIUS} maxBarSize={40} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="overflow-x-auto border border-slate-200 rounded-lg mt-5">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
                <tr>
                  <th className="text-left px-3 py-2 font-semibold">Metric</th>
                  <th className="text-left px-3 py-2 font-semibold w-20">Unit</th>
                  <th className="text-left px-3 py-2 font-semibold w-24">Baseline</th>
                  <th className="text-left px-3 py-2 font-semibold w-24">Target</th>
                  <th className="text-left px-3 py-2 font-semibold w-32">Post-Intervention</th>
                  <th className="text-left px-3 py-2 font-semibold w-28">Gap Closed</th>
                </tr>
              </thead>
              <tbody>
                {computed.map((c, i) => (
                  <tr key={c.m.id || i} className="border-t border-slate-100" data-testid={`metric-result-row-${i}`}>
                    <td className="px-3 py-2 text-slate-700">{c.m.name || <span className="text-slate-400 italic">Untitled</span>}</td>
                    <td className="px-3 py-2 text-slate-500">{c.m.unit || "—"}</td>
                    <td className="px-3 py-2 text-slate-600">{isNaN(c.baseline) ? "—" : c.baseline}</td>
                    <td className="px-3 py-2 text-slate-600">{isNaN(c.targetV) ? "—" : c.targetV}</td>
                    <td className="px-3 py-2">
                      <input type="number" value={results[c.m.id] ?? ""} onChange={(e) => setResult(c.m.id, e.target.value)} placeholder="result" className={`${inputCls} py-1`} data-testid={`metric-post-${i}`} />
                    </td>
                    <td className="px-3 py-2 font-semibold text-emerald-600">{c.closePct === null ? "—" : `${c.closePct}%`}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-4">
            <label className="block text-sm font-medium text-slate-700 mb-1">Post-Intervention Metrics Summary</label>
            <Textarea data-testid="post-metrics-input" rows={3} value={postMetrics || ""} onChange={(e) => onPostMetrics(e.target.value)} placeholder="Qualitative results, data sources…" />
          </div>
        </>
      )}
    </SectionCard>
  );
}

function ObjectiveImpactCard({ objectives, impactFor, setImpact }) {
  return (
    <SectionCard
      title="Objective Impact Tracker"
      desc="Learning objectives inherited from Phase 3 (ADDIE Design). Record Kirkpatrick Level 2 (learning) and Level 3 (behavior) results for each."
      right={<div className="flex items-center gap-2 text-emerald-600 text-sm font-medium"><ListChecks className="h-4 w-4" /> Inherited from Phase 3</div>}
      testid="phase6-objective-impact"
    >
      {objectives.length === 0 ? (
        <p data-testid="objective-impact-empty" className="text-sm text-slate-400">
          No learning objectives defined in Phase 3 (ADDIE Design) yet. Objectives added there will appear here automatically.
        </p>
      ) : (
        <div className="overflow-x-auto border border-slate-200 rounded-lg">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
              <tr>
                <th className="text-left px-3 py-2 font-semibold w-10">#</th>
                <th className="text-left px-3 py-2 font-semibold w-28">Bloom Level</th>
                <th className="text-left px-3 py-2 font-semibold">Learning Objective</th>
                <th className="text-left px-3 py-2 font-semibold w-44">L2: Assessment Score</th>
                <th className="text-left px-3 py-2 font-semibold w-56">L3: Behavior Metric</th>
              </tr>
            </thead>
            <tbody>
              {objectives.map((o, i) => {
                const cur = impactFor(o, i);
                return (
                <tr key={o.id || i} className="border-t border-slate-100 align-top" data-testid={`objective-impact-row-${i}`}>
                  <td className="px-3 py-3 text-slate-400">{i + 1}</td>
                  <td className="px-3 py-3"><span className="inline-block text-xs font-medium px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">{o.bloom_level}</span></td>
                  <td className="px-3 py-3 text-slate-700">{o.text || <span className="text-slate-400 italic">Untitled objective</span>}</td>
                  <td className="px-3 py-3"><input data-testid={`objective-l2-${i}`} value={cur.level2 || ""} onChange={(e) => setImpact(o, i, "level2", e.target.value)} placeholder="e.g. 88% pass" className={inputCls} /></td>
                  <td className="px-3 py-3"><input data-testid={`objective-l3-${i}`} value={cur.level3 || ""} onChange={(e) => setImpact(o, i, "level3", e.target.value)} placeholder="e.g. 30% more discovery calls logged" className={inputCls} /></td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </SectionCard>
  );
}

function KirkpatrickCard({ kirk, setKirk }) {
  const levels = [["reaction", "Reaction"], ["learning", "Learning"], ["behavior", "Behavior"], ["results", "Results"]];
  return (
    <SectionCard title="Evaluation Plan — Kirkpatrick's 4 Levels" desc="Define success criteria, planned method and timing for each evaluation level (Design Document)." testid="phase6-kirkpatrick">
      <div className="overflow-x-auto border border-slate-200 rounded-lg">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
            <tr>
              <th className="text-left px-3 py-2 font-semibold w-28">Level</th>
              <th className="text-left px-3 py-2 font-semibold">Success Criteria</th>
              <th className="text-left px-3 py-2 font-semibold">Planned Method(s)</th>
              <th className="text-left px-3 py-2 font-semibold w-32">Timing</th>
            </tr>
          </thead>
          <tbody>
            {levels.map(([key, label]) => {
              const k = kirk[key] || {};
              return (
                <tr key={key} className="border-t border-slate-100" data-testid={`kirk-row-${key}`}>
                  <td className="px-3 py-2 font-medium text-slate-700">{label}</td>
                  <td className="px-3 py-2"><input value={k.criteria || ""} onChange={(e) => setKirk(key, "criteria", e.target.value)} placeholder="Success criteria" className="w-full outline-none bg-transparent" data-testid={`kirk-${key}-criteria`} /></td>
                  <td className="px-3 py-2"><input value={k.method || ""} onChange={(e) => setKirk(key, "method", e.target.value)} placeholder="Method(s)" className="w-full outline-none bg-transparent" /></td>
                  <td className="px-3 py-2"><input value={k.timing || ""} onChange={(e) => setKirk(key, "timing", e.target.value)} placeholder="Timing" className="w-full outline-none bg-transparent" /></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </SectionCard>
  );
}

export default function Phase6() {
  const { phaseData: pd, setPhase } = useProject();
  const p = pd.phase6 || { post_metrics: "", notes: "" };
  const p1 = pd.phase1 || {};
  const set = (k, v) => setPhase("phase6", { ...p, [k]: v });

  const metrics = getMetrics(p1);
  const results = p.metric_results || {};
  const setResult = (id, value) => setPhase("phase6", { ...p, metric_results: { ...results, [id]: value } });

  const kirk = p.kirkpatrick || {};
  const setKirk = (level, field, v) => setPhase("phase6", { ...p, kirkpatrick: { ...kirk, [level]: { ...(kirk[level] || {}), [field]: v } } });

  const computed = computeMetricResults(metrics, results);
  const withPost = computed.filter((c) => !isNaN(c.post) && c.closePct !== null);
  const avgClosed = withPost.length ? Math.round(withPost.reduce((s, c) => s + c.closePct, 0) / withPost.length) : null;

  const chartData = computed
    .filter((c) => !isNaN(c.baseline) || !isNaN(c.targetV) || !isNaN(c.post))
    .map((c) => ({
      name: c.m.name || "Metric",
      Baseline: isNaN(c.baseline) ? 0 : c.baseline,
      "Post-Intervention": isNaN(c.post) ? 0 : c.post,
      Target: isNaN(c.targetV) ? 0 : c.targetV,
    }));

  const kpis = [
    { label: "Metrics Tracked", value: metrics.length, icon: Gauge, sub: "from Phase 1" },
    { label: "Measured", value: `${withPost.length}/${metrics.length}`, icon: Flag, sub: "post-intervention" },
    { label: "Avg Gap Closed", value: avgClosed === null ? "—" : `${avgClosed}%`, icon: Target, sub: "across metrics" },
  ];

  // Inherit learning objectives created in Phase 3 (ADDIE Design).
  const objectives = pd.phase3?.addie?.design?.objectives || [];
  const impact = p.objective_impact || {};
  const impactFor = (obj, idx) => impact[obj.id] || impact[idx] || {};
  const setImpact = (obj, idx, key, value) => {
    const storeKey = obj.id ?? idx;
    const current = impact[obj.id] || impact[idx] || {};
    setPhase("phase6", { ...p, objective_impact: { ...impact, [storeKey]: { ...current, [key]: value } } });
  };

  return (
    <div className="space-y-6 max-w-5xl">
      <EvalKpis kpis={kpis} />
      <MetricsEvalCard metrics={metrics} computed={computed} results={results} setResult={setResult} chartData={chartData} postMetrics={p.post_metrics} onPostMetrics={(v) => set("post_metrics", v)} />
      <ObjectiveImpactCard objectives={objectives} impactFor={impactFor} setImpact={setImpact} />

      <SectionCard title="Evaluation Notes & Recommendations" testid="phase6-notes">
        <Textarea data-testid="eval-notes-input" rows={4} value={p.notes || ""} onChange={(e) => set("notes", e.target.value)} placeholder="Lessons learned, sustainment plan, next steps…" />
      </SectionCard>

      <SupportingMaterials phaseKey="phase6" />
    </div>
  );
}
